import React, { useState, useEffect, useCallback } from 'react';
import { 
  Wallet, 
  Plus, 
  Minus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  XCircle,
  CreditCard,
  Banknote,
  Bitcoin,
  Eye,
  EyeOff,
  Shield,
  Info,
  Layers,
  Calendar,
  Percent,
  Calculator,
  ArrowRight,
  Lock,
  X
} from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';

interface StakingPageProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  onStake: (asset: string, amount: number, duration: number) => Promise<boolean>;
}

interface StakingAsset {
  id: string;
  name: string;
  symbol: string;
  icon: React.ReactNode;
  apy: number;
  minStake: number;
  lockPeriods: number[]; // in days
  totalStaked: number;
  balance?: number;
  price?: number;
}

const StakingPage: React.FC<StakingPageProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  onStake
}) => {
  const { userStakes, addUserStake, calculateCurrentEarnings, cancelUserStake, fetchUserStakes } = useDatabase();
  
  // State for staking form
  const [stakingAmount, setStakingAmount] = useState('');
  const [stakingDuration, setStakingDuration] = useState(30);
  const [selectedAsset, setSelectedAsset] = useState<StakingAsset | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // State for staking operations
  const [isStaking, setIsStaking] = useState(false);
  const [stakingMessage, setStakingMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  
  // State for expanded asset details
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  
  // State for calculator
  const [calculatorAsset, setCalculatorAsset] = useState('btc-stake');
  const [calculatorAmount, setCalculatorAmount] = useState('1');
  const [calculatorDuration, setCalculatorDuration] = useState('30');
  const [calculatorResults, setCalculatorResults] = useState({
    estimatedEarnings: 0,
    estimatedEarningsUsd: 0,
    daily: 0,
    weekly: 0,
    monthly: 0,
    yearly: 0
  });

  // Available staking assets
  const [availableStakingAssets, setAvailableStakingAssets] = useState<StakingAsset[]>([
    {
      id: 'btc-stake',
      name: 'Bitcoin',
      symbol: 'BTC',
      icon: <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold">BTC</div>,
      apy: 5.5,
      minStake: 0.001,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 1250.75,
      balance: btcBalance,
      price: currentBtcPrice
    },
    {
      id: 'usdt-stake',
      name: 'Tether',
      symbol: 'USDT',
      icon: <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold">USDT</div>,
      apy: 12.0,
      minStake: 100,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 5750000,
      balance: usdtBalance,
      price: 1
    },
    {
      id: 'eth-stake',
      name: 'Ethereum',
      symbol: 'ETH',
      icon: <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold">ETH</div>,
      apy: 7.2,
      minStake: 0.01,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 32500.5,
      balance: 0,
      price: 3500
    },
    {
      id: 'sol-stake',
      name: 'Solana',
      symbol: 'SOL',
      icon: <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold">SOL</div>,
      apy: 9.8,
      minStake: 1,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 185000,
      balance: 0,
      price: 150
    }
  ]);

  // Fetch user stakes on mount
  useEffect(() => {
    fetchUserStakes();
  }, [fetchUserStakes]);

  // Update asset balances when they change
  useEffect(() => {
    setAvailableStakingAssets(prev => 
      prev.map(asset => {
        if (asset.symbol === 'BTC') {
          return { ...asset, balance: btcBalance, price: currentBtcPrice };
        } else if (asset.symbol === 'USDT') {
          return { ...asset, balance: usdtBalance, price: 1 };
        }
        return asset;
      })
    );
  }, [btcBalance, usdtBalance, currentBtcPrice]);

  // Clear staking message after 5 seconds
  useEffect(() => {
    if (stakingMessage) {
      const timer = setTimeout(() => {
        setStakingMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [stakingMessage]);

  // Update calculator results when inputs change
  useEffect(() => {
    const updateCalculatorResults = () => {
      const asset = availableStakingAssets.find(a => a.id === calculatorAsset);
      if (!asset) return;

      const amount = parseFloat(calculatorAmount) || 0;
      const days = parseInt(calculatorDuration) || 30;
      const apy = asset.apy;

      // Calculate daily rate
      const dailyRate = apy / 365 / 100;
      
      // Calculate earnings
      const estimatedEarnings = amount * dailyRate * days;
      const estimatedEarningsUsd = estimatedEarnings * (asset.price || 0);
      
      // Calculate periodic earnings
      const daily = amount * dailyRate;
      const weekly = daily * 7;
      const monthly = daily * 30;
      const yearly = amount * (apy / 100);
      
      setCalculatorResults({
        estimatedEarnings,
        estimatedEarningsUsd,
        daily,
        weekly,
        monthly,
        yearly
      });
    };
    
    updateCalculatorResults();
  }, [calculatorAsset, calculatorAmount, calculatorDuration, availableStakingAssets]);

  // Calculate estimated return based on amount, duration, and APY
  const calculateEstimatedReturn = (amount: number, durationDays: number, apy: number) => {
    // Convert APY to daily rate
    const dailyRate = apy / 365 / 100;
    
    // Calculate interest for the staking period
    const interest = amount * dailyRate * durationDays;
    
    return interest;
  };

  // Handle stake button click
  const handleStakeClick = (asset: StakingAsset) => {
    setSelectedAsset(asset);
    setStakingAmount('');
    setStakingDuration(asset.lockPeriods[0]);
    setShowConfirmationModal(true);
  };

  // Check if user already has an active stake for the selected asset
  const hasActiveStakeForAsset = (assetSymbol: string) => {
    return userStakes.some(stake => 
      stake.asset_symbol === assetSymbol && 
      stake.status === 'active'
    );
  };

  // Confirm stake
  const confirmStake = async () => {
    if (!selectedAsset) return;
    
    const amount = parseFloat(stakingAmount);
    
    // Validate input
    if (isNaN(amount) || amount <= 0) {
      setStakingMessage({
        type: 'error',
        text: 'Please enter a valid amount'
      });
      return;
    }
    
    // Check minimum stake
    if (amount < selectedAsset.minStake) {
      setStakingMessage({
        type: 'error',
        text: `Minimum stake amount is ${selectedAsset.minStake} ${selectedAsset.symbol}`
      });
      return;
    }
    
    // Check balance
    if (selectedAsset.balance && amount > selectedAsset.balance) {
      setStakingMessage({
        type: 'error',
        text: `Insufficient ${selectedAsset.symbol} balance`
      });
      return;
    }
    
    // Check if user already has an active stake for this asset
    if (hasActiveStakeForAsset(selectedAsset.symbol)) {
      setStakingMessage({
        type: 'error',
        text: `You already have an active stake for ${selectedAsset.symbol}. Please wait for it to complete before creating a new one.`
      });
      return;
    }
    
    setIsStaking(true);
    
    try {
      // Call the addUserStake function
      const success = await addUserStake(
        selectedAsset.symbol,
        amount,
        selectedAsset.apy,
        stakingDuration
      );
      
      if (success) {
        setStakingMessage({
          type: 'success',
          text: `Successfully staked ${amount} ${selectedAsset.symbol} for ${stakingDuration} days`
        });
        
        // Close modal and reset form
        setShowConfirmationModal(false);
        setStakingAmount('');
        setSelectedAsset(null);
        
        // Call the onStake prop to update balances
        await onStake(selectedAsset.symbol, amount, stakingDuration);
        
        // Refresh user stakes
        await fetchUserStakes();
      } else {
        throw new Error('Staking failed');
      }
    } catch (error: any) {
      setStakingMessage({
        type: 'error',
        text: error.message || 'Failed to stake. Please try again.'
      });
    } finally {
      setIsStaking(false);
    }
  };

  // Handle max button click
  const handleMaxClick = () => {
    if (selectedAsset?.balance) {
      setStakingAmount(selectedAsset.balance.toString());
    }
  };

  // Toggle asset details expansion
  const toggleAssetDetails = (assetId: string) => {
    if (expandedAssetId === assetId) {
      setExpandedAssetId(null);
    } else {
      setExpandedAssetId(assetId);
    }
  };

  // Calculate time remaining for a stake
  const calculateTimeRemaining = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return 'Completed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h remaining`;
  };

  // Calculate progress percentage for a stake
  const calculateProgress = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    if (elapsed <= 0) return 0;
    if (elapsed >= total) return 100;
    
    return (elapsed / total) * 100;
  };

  // Get active stakes
  const activeStakes = userStakes.filter(stake => stake.status === 'active');
  
  // Get completed stakes
  const completedStakes = userStakes.filter(stake => stake.status === 'completed');

  return (
    <div className="min-h-screen p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent mb-2">
          Staking
        </h1>
        <p className="text-slate-400">
          Earn passive income by staking your crypto assets. Choose from various staking options with competitive APY rates.
        </p>
      </div>

      {/* Status Messages */}
      {stakingMessage && (
        <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
          stakingMessage.type === 'success' 
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {stakingMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          {stakingMessage.text}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Available Staking Assets */}
        <div className="lg:col-span-2 space-y-8">
          {/* Available Staking Assets */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Layers size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white">Available Staking Assets</h2>
              </div>
              <div className="text-sm text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full">
                {availableStakingAssets.length} Assets
              </div>
            </div>
            
            <div className="space-y-4">
              {availableStakingAssets.map((asset) => {
                const hasActiveStake = hasActiveStakeForAsset(asset.symbol);
                
                return (
                  <div key={asset.id} className="bg-slate-900/40 rounded-xl border border-slate-700/50 overflow-hidden">
                    {/* Asset Header */}
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {asset.icon}
                        <div>
                          <div className="font-semibold text-white">{asset.name}</div>
                          <div className="text-sm text-slate-400">{asset.symbol}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <div className="text-sm text-slate-400">APY</div>
                          <div className="text-emerald-400 font-bold">{asset.apy}%</div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-sm text-slate-400">Balance</div>
                          <div className="text-white font-medium">
                            {asset.balance?.toFixed(asset.symbol === 'USDT' ? 2 : 6)} {asset.symbol}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => toggleAssetDetails(asset.id)}
                          className="w-8 h-8 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center transition-colors"
                        >
                          {expandedAssetId === asset.id ? (
                            <Minus size={18} className="text-slate-400" />
                          ) : (
                            <Plus size={18} className="text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedAssetId === asset.id && (
                      <div className="p-4 border-t border-slate-700/50 bg-slate-800/30">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-slate-400 mb-1">Minimum Stake</div>
                            <div className="text-white font-medium">{asset.minStake} {asset.symbol}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-400 mb-1">Total Staked</div>
                            <div className="text-white font-medium">{asset.totalStaked.toLocaleString()} {asset.symbol}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-400 mb-1">Value</div>
                            <div className="text-white font-medium">${(asset.price || 0).toLocaleString()}</div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="text-sm text-slate-400 mb-2">Available Lock Periods</div>
                          <div className="flex flex-wrap gap-2">
                            {asset.lockPeriods.map((period) => (
                              <div key={period} className="px-3 py-1 bg-slate-800 rounded-lg text-sm text-slate-300 border border-slate-700/50">
                                {period} days
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleStakeClick(asset)}
                            disabled={!asset.balance || asset.balance < asset.minStake || hasActiveStake}
                            className={`bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-slate-700 disabled:to-slate-800 text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 shadow-lg shadow-purple-500/25 flex items-center gap-2 ${
                              hasActiveStake ? 'cursor-not-allowed opacity-70' : ''
                            }`}
                          >
                            <Layers size={16} />
                            {hasActiveStake ? 'Already Staked' : `Stake ${asset.symbol}`}
                          </button>
                        </div>
                        
                        {hasActiveStake && (
                          <div className="mt-3 text-xs text-amber-400 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            <span>You already have an active stake for this asset</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Active Stakes */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <TrendingUp size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Your Active Stakes</h2>
            </div>
            
            {activeStakes.length > 0 ? (
              <div className="space-y-4">
                {activeStakes.map((stake) => {
                  const asset = availableStakingAssets.find(a => a.symbol === stake.asset_symbol);
                  const progress = calculateProgress(stake.start_date, stake.end_date);
                  const timeRemaining = calculateTimeRemaining(stake.end_date);
                  const currentEarnings = calculateCurrentEarnings(stake);
                  
                  return (
                    <div key={stake.id} className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {asset?.icon || (
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                              <span className="text-white font-bold">{stake.asset_symbol.substring(0, 2)}</span>
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-white">{asset?.name || stake.asset_symbol}</div>
                            <div className="text-xs text-slate-400">{stake.staked_amount.toFixed(stake.asset_symbol === 'USDT' ? 2 : 6)} {stake.asset_symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-emerald-400 font-bold">{stake.apy_rate}% APY</div>
                          <div className="text-xs text-slate-400">
                            {new Date(stake.end_date).toLocaleDateString()} end date
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full h-2 bg-slate-700 rounded-full mb-2">
                        <div 
                          className="h-2 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      
                      <div className="flex justify-between text-xs text-slate-400 mb-3">
                        <div>Started: {new Date(stake.start_date).toLocaleDateString()}</div>
                        <div>Ends: {new Date(stake.end_date).toLocaleDateString()}</div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-slate-400">Earned so far</div>
                          <div className="text-emerald-400 font-medium">{currentEarnings.toFixed(6)} {stake.asset_symbol}</div>
                        </div>
                        <div className="text-sm">
                          <Clock size={14} className="text-slate-400 inline mr-1" />
                          <span className="text-slate-300">{timeRemaining}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Layers size={48} className="mx-auto mb-4 opacity-50" />
                <p className="mb-2">No active stakes</p>
                <p className="text-sm text-slate-600">Start staking your assets to earn passive income</p>
              </div>
            )}
          </div>
          
          {/* Completed Stakes */}
          {completedStakes.length > 0 && (
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <CheckCircle size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white">Completed Stakes</h2>
              </div>
              
              <div className="space-y-4">
                {completedStakes.map((stake) => {
                  const asset = availableStakingAssets.find(a => a.symbol === stake.asset_symbol);
                  
                  return (
                    <div key={stake.id} className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {asset?.icon || (
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                              <span className="text-white font-bold">{stake.asset_symbol.substring(0, 2)}</span>
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-white">{asset?.name || stake.asset_symbol}</div>
                            <div className="text-xs text-slate-400">{stake.staked_amount.toFixed(stake.asset_symbol === 'USDT' ? 2 : 6)} {stake.asset_symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-emerald-400 font-bold">{stake.apy_rate}% APY</div>
                          <div className="text-xs text-slate-400">
                            {new Date(stake.start_date).toLocaleDateString()} - {new Date(stake.end_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-slate-400">Total Earned</div>
                          <div className="text-emerald-400 font-medium">{stake.earned_amount.toFixed(6)} {stake.asset_symbol}</div>
                        </div>
                        <div className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-lg text-xs font-medium">
                          Completed
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Staking Benefits */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Info size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Staking Benefits</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25 mb-4">
                  <DollarSign size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Passive Income</h3>
                <p className="text-slate-400 text-sm">Earn regular rewards on your crypto holdings without active trading.</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25 mb-4">
                  <Percent size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Competitive APY</h3>
                <p className="text-slate-400 text-sm">Get some of the highest yields in the market with our optimized staking pools.</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25 mb-4">
                  <Lock size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Flexible Terms</h3>
                <p className="text-slate-400 text-sm">Choose from various lock periods to match your investment strategy.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Active Stakes & Calculator */}
        <div className="space-y-8">
          {/* Staking Calculator */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/25">
                <Calculator size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Staking Calculator</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Select Asset</label>
                <select 
                  className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all custom-select"
                  value={calculatorAsset}
                  onChange={(e) => setCalculatorAsset(e.target.value)}
                >
                  {availableStakingAssets.map(asset => (
                    <option key={asset.id} value={asset.id}>{asset.name} ({asset.apy}% APY)</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Amount</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  value={calculatorAmount}
                  onChange={(e) => setCalculatorAmount(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Lock Period (Days)</label>
                <select 
                  className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all custom-select"
                  value={calculatorDuration}
                  onChange={(e) => setCalculatorDuration(e.target.value)}
                >
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">365 days</option>
                </select>
              </div>
              
              <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30 mt-6">
                <div className="text-center mb-4">
                  <div className="text-sm text-slate-400 mb-1">Estimated Earnings</div>
                  <div className="text-2xl font-bold text-emerald-400">
                    {calculatorResults.estimatedEarnings.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                  </div>
                  <div className="text-sm text-slate-300">≈ ${calculatorResults.estimatedEarningsUsd.toFixed(2)}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-slate-400">Daily</div>
                    <div className="text-white font-mono">
                      {calculatorResults.daily.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400">Weekly</div>
                    <div className="text-white font-mono">
                      {calculatorResults.weekly.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400">Monthly</div>
                    <div className="text-white font-mono">
                      {calculatorResults.monthly.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400">Yearly</div>
                    <div className="text-white font-mono">
                      {calculatorResults.yearly.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Staking FAQ */}
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Info size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Staking FAQ</h2>
            </div>
            
            <div className="space-y-4">
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <h3 className="font-medium text-white mb-2">What is staking?</h3>
                <p className="text-sm text-slate-400">Staking is a way to earn passive income by locking up your crypto assets for a specific period of time.</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <h3 className="font-medium text-white mb-2">How are rewards calculated?</h3>
                <p className="text-sm text-slate-400">Rewards are calculated based on the APY rate, the amount staked, and the duration of the stake.</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <h3 className="font-medium text-white mb-2">Can I unstake early?</h3>
                <p className="text-sm text-slate-400">No, once you stake your assets, they will be locked for the entire duration of the staking period. Early unstaking is not possible.</p>
              </div>
              
              <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/50">
                <h3 className="font-medium text-white mb-2">When do I receive my rewards?</h3>
                <p className="text-sm text-slate-400">Rewards are calculated continuously but are paid out at the end of the staking period. Both your initial stake and earned rewards will be returned to your wallet automatically.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Staking Confirmation Modal */}
      {showConfirmationModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Confirm Staking</h3>
              <button
                onClick={() => setShowConfirmationModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-4">
                  {selectedAsset.icon}
                  <div>
                    <div className="font-medium text-white">{selectedAsset.name}</div>
                    <div className="text-sm text-slate-400">{selectedAsset.apy}% APY</div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Amount to Stake</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={stakingAmount}
                        onChange={(e) => setStakingAmount(e.target.value)}
                        placeholder={`Min ${selectedAsset.minStake} ${selectedAsset.symbol}`}
                        className="w-full bg-slate-900/50 text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                        <button
                          onClick={handleMaxClick}
                          className="text-xs text-purple-400 hover:text-purple-300 transition-colors px-2 py-1 rounded bg-purple-400/10"
                        >
                          MAX
                        </button>
                        <span className="text-slate-400">{selectedAsset.symbol}</span>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Available: {selectedAsset.balance?.toFixed(selectedAsset.symbol === 'USDT' ? 2 : 6)} {selectedAsset.symbol}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Staking Period</label>
                    <select
                      value={stakingDuration}
                      onChange={(e) => setStakingDuration(parseInt(e.target.value))}
                      className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all custom-select"
                    >
                      {selectedAsset.lockPeriods.map((period) => (
                        <option key={period} value={period}>{period} days</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Estimated Returns */}
              <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator size={16} className="text-purple-400" />
                  <span className="text-white font-medium">Estimated Returns</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">APY Rate</span>
                    <span className="text-emerald-400">{selectedAsset.apy}%</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Lock Period</span>
                    <span className="text-white">{stakingDuration} days</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Estimated Earnings</span>
                    <span className="text-emerald-400">
                      {calculateEstimatedReturn(
                        parseFloat(stakingAmount) || 0, 
                        stakingDuration, 
                        selectedAsset.apy
                      ).toFixed(6)} {selectedAsset.symbol}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Unlock Date</span>
                    <span className="text-white">
                      {new Date(Date.now() + stakingDuration * 24 * 60 * 60 * 1000).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Check for existing active stake */}
              {hasActiveStakeForAsset(selectedAsset.symbol) && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-400 flex items-start gap-3">
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium mb-1">Active Stake Exists</p>
                    <p>You already have an active stake for {selectedAsset.symbol}. Please wait for it to complete before creating a new one.</p>
                  </div>
                </div>
              )}
              
              {/* Warning */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-400 flex items-start gap-3">
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Important Information</p>
                  <p>Once staked, your assets will be locked for the entire duration of the staking period. Early unstaking is not possible.</p>
                </div>
              </div>
              
              {/* Action Button - Removed the cancel button and made the confirm button full width */}
              <button
                onClick={confirmStake}
                disabled={isStaking || !stakingAmount || parseFloat(stakingAmount) <= 0 || hasActiveStakeForAsset(selectedAsset.symbol)}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2"
              >
                {isStaking ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Layers size={18} />
                    Confirm Stake
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingPage;